/**
 * A barcode decoder, whether or not the browser ships one.
 *
 * `BarcodeDetector` is Chromium's. Safari and Firefox do not have it, and on
 * iOS *every* browser is WebKit underneath — Chrome for iOS included — so no
 * iPhone has it whatever the icon says. What iOS does have is the camera:
 * `getUserMedia` and a live `<video>` work there already. Only the decoder was
 * missing, and a decoder is just code.
 *
 * So this returns one either way. Native where it exists, zbar compiled to
 * WASM where it does not, behind the single method the loop in
 * `pages/ScanProduct.tsx` calls. Nothing above here knows which it got.
 *
 * The trade that used to make this not worth doing was a megabyte of decoder on
 * a screen most people reach twice a week. It is 236KB, it is fetched only by
 * the browsers that need it, and only when this module is first imported — so
 * Chromium pays nothing at all and never downloads it.
 */

/** The one method the loop needs, and the shape `BarcodeDetector` already has. */
export interface BarcodeDecoder {
  detect(video: HTMLVideoElement): Promise<{ rawValue: string }[]>;
}

/** Not in every browser's lib.dom yet. */
interface BarcodeDetectorLike {
  detect(source: CanvasImageSource): Promise<{ rawValue: string }[]>;
}

/** What a food packet actually carries. Mirrored in the worker for zbar. */
const FORMATS = ["ean_13", "ean_8", "upc_a", "upc_e"];

/**
 * The long edge the WASM decoder gets, in pixels.
 *
 * Not the full frame. A barcode that will not decode at 640 will not decode at
 * 2000 either — it is bars against a background and the limit is whether the
 * narrowest bar survives, which framing decides long before resolution does.
 * The frame a phone actually produces is ten times the pixels, all of which
 * would be converted to grey and scanned line by line, eight times a second.
 *
 * The *photo* is still sent whole and uncropped — see lib/capture.ts. This is
 * the throwaway copy the decoder looks at, not the thing anyone keeps.
 */
const DECODE_EDGE = 640;

function nativeDecoder(): BarcodeDecoder | null {
  const Detector = (
    window as unknown as { BarcodeDetector?: new (options?: object) => BarcodeDetectorLike }
  ).BarcodeDetector;
  if (!Detector) return null;

  // Truthiness above rather than `in`, so this asks exactly the question the
  // constructor answers. A property present but undefined would otherwise
  // produce a decoder that throws on its first frame.
  const detector = new Detector({ formats: FORMATS });
  return { detect: (video) => detector.detect(video) };
}

/**
 * zbar in a worker, fed frames through a canvas.
 *
 * A `<video>` cannot cross to a worker, so the pixels have to be read out here
 * — `drawImage` into a canvas, then `getImageData`. That readback is the whole
 * of this thread's per-frame cost; the decode itself happens elsewhere.
 *
 * Resolves only once the worker says it has a scanner. A browser where the
 * WASM will not load must fall back to the shutter rather than promise a
 * scanner that is never going to run.
 */
async function wasmDecoder(): Promise<BarcodeDecoder | null> {
  if (typeof Worker === "undefined") return null;

  const worker = new Worker(new URL("./barcode.worker.ts", import.meta.url), {
    type: "module",
  });

  // One canvas for the life of the page. A fresh one per frame is a few hundred
  // megabytes of garbage a minute at this cadence, and `willReadFrequently` is
  // the documented hint for exactly this pattern — repeated readback, no
  // compositing — which keeps the surface off the GPU where reading it back is
  // the expensive direction.
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (!context) {
    worker.terminate();
    return null;
  }

  const pending = new Map<number, (codes: string[]) => void>();
  let nextId = 1;

  const settle = (id: number, codes: string[]) => {
    const waiting = pending.get(id);
    if (waiting) {
      pending.delete(id);
      waiting(codes);
    }
  };

  const started = new Promise<boolean>((resolve) => {
    worker.addEventListener(
      "message",
      (event: MessageEvent<{ ready?: boolean; id?: number; codes?: string[] }>) => {
        const { ready, id, codes } = event.data;
        if (ready !== undefined) resolve(ready);
        if (id !== undefined) settle(id, codes ?? []);
      },
    );
    // A worker that dies takes every frame waiting on it with it. Answering
    // them empty rather than leaving them pending is what stops the loop —
    // which awaits each attempt before booking the next — from simply stopping.
    worker.addEventListener("error", () => {
      resolve(false);
      for (const waiting of pending.values()) waiting([]);
      pending.clear();
    });
  });

  if (!(await started)) {
    worker.terminate();
    return null;
  }

  return {
    async detect(video) {
      const { videoWidth, videoHeight } = video;
      if (!videoWidth || !videoHeight) return [];

      const shrink = Math.min(1, DECODE_EDGE / Math.max(videoWidth, videoHeight));
      canvas.width = Math.round(videoWidth * shrink);
      canvas.height = Math.round(videoHeight * shrink);
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      const { data, width, height } = context.getImageData(0, 0, canvas.width, canvas.height);

      const id = nextId;
      nextId += 1;
      // `getImageData` hands back a fresh buffer every call, so transferring it
      // costs nothing and copies nothing — 1.2MB a frame that never moves.
      const codes = await new Promise<string[]>((resolve) => {
        pending.set(id, resolve);
        worker.postMessage({ id, pixels: data.buffer, width, height }, [data.buffer]);
      });
      return codes.map((rawValue) => ({ rawValue }));
    },
  };
}

/**
 * Resolved once, and kept.
 *
 * The loop's effect re-runs whenever the screen changes what it is doing —
 * an answer arrives, a photo freezes the feed — and building a worker and
 * recompiling the WASM on each of those would be absurd. One decoder per page.
 */
let resolving: Promise<BarcodeDecoder | null> | undefined;

export function getDecoder(): Promise<BarcodeDecoder | null> {
  resolving ??= Promise.resolve(nativeDecoder()).then(
    (native) => native ?? wasmDecoder().catch(() => null),
  );
  return resolving;
}
